import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import PageTitle from '../../components/PageTitle';
import { lookupHeartbeatRaw } from '../../api';

const { Text } = Typography;

const fmtNum = (v) => (v == null || Number.isNaN(Number(v)) ? '-' : Number(v).toFixed(3));

const columns = [
  { title: 'ID', dataIndex: 'id', width: 90 },
  {
    title: '时间',
    dataIndex: 'received_at',
    width: 180,
    render: (v) => (v ? String(v).replace('T', ' ').slice(0, 23) : '-'),
  },
  { title: 'meter_no', dataIndex: 'meter_no', width: 140 },
  {
    title: '剩余(度)',
    dataIndex: 'remaining_power',
    width: 100,
    render: fmtNum,
  },
  {
    title: '总用(度)',
    dataIndex: 'total_power',
    width: 100,
    render: fmtNum,
  },
  {
    title: '信号',
    dataIndex: 'signal',
    width: 70,
    render: (v, row) => (v != null && v !== 0 ? v : row.signal_hex || '-'),
  },
  {
    title: '闸位',
    dataIndex: 'switch_text',
    width: 70,
    render: (v) => v || '-',
  },
  {
    title: '状态',
    dataIndex: 'parse_ok',
    width: 80,
    render: (ok) => (ok ? <Tag color="success">正常</Tag> : <Tag color="warning">异常</Tag>),
  },
  {
    title: 'raw_hex',
    dataIndex: 'raw_hex',
    ellipsis: true,
    render: (v) => (v ? <Text code copyable={{ text: v }}>{v}</Text> : '-'),
  },
];

const HeartbeatRawQuery = () => {
  const [meterNo, setMeterNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const runQuery = async () => {
    const q = String(meterNo || '').trim();
    if (!q) {
      message.warning('请输入当前表号或 MAC');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await lookupHeartbeatRaw(q);
      if (!res?.success) {
        setError(res?.msg || '查询失败');
        return;
      }
      setResult(res.data || null);
      const count = res.data?.count ?? 0;
      if (count === 0) {
        message.info('该时间范围内无原始心跳');
      } else {
        message.success(`查到 ${count} 条`);
      }
    } catch (e) {
      setError(e?.response?.data?.msg || e?.message || '查询失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageTitle title="心跳原始查询" />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="近 24 小时原始心跳（mqtt_heartbeat_raw）"
        description="输入当前表号（如 YJ0014434）或 MAC（如 8A9AC32CC9EB）。服务端自动匹配多键并解析 54 帧剩余/总用/信号/闸位；异常帧标黄，勿当有效读数。原始表仅保留约 24 小时。"
      />

      <Card title="查询条件" size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 640 }}>
          <div>
            <Text type="secondary">当前表号 / MAC</Text>
            <Input
              value={meterNo}
              onChange={(e) => setMeterNo(e.target.value)}
              placeholder="如 YJ0014434 或 8A9AC32CC9EB"
              disabled={loading}
              onPressEnter={runQuery}
            />
          </div>
          <Button type="primary" loading={loading} onClick={runQuery}>
            查询最近 24 小时
          </Button>
        </Space>
      </Card>

      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

      {result ? (
        <>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Space direction="vertical" size={4}>
              <Text>输入：{result.input_meter_no}</Text>
              <Text>
                设备：
                {result.device_found
                  ? `ID ${result.device?.id} / ${result.device?.current_meter_no || '-'} / MAC ${result.device?.device_mac || '-'}`
                  : '未在 device_info 中找到（将直接用输入值查心跳）'}
              </Text>
              <Text type="secondary">匹配键：{(result.heartbeat_keys || []).join(', ') || '-'}</Text>
              <Text type="secondary">
                时间范围：{result.start} ~ {result.end}（共 {result.count ?? 0} 条）
              </Text>
            </Space>
          </Card>

          <Card title="原始心跳列表" size="small">
            <Table
              rowKey="id"
              size="small"
              columns={columns}
              dataSource={result.rows || []}
              pagination={{ pageSize: 50, showSizeChanger: true }}
              scroll={{ x: 1200 }}
            />
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default HeartbeatRawQuery;
